import { createAsyncThunk } from '@reduxjs/toolkit';
import type { IncomeCalculationDocument } from '@snapptinc/fraud-platform';
import { format } from 'date-fns';
import { DATE_PICKER_FORMAT } from 'src/components/molecules/DatePicker';
import { IncomeVerification } from 'src/ts/interfaces/incomeVerification';
import api from './api';
import {
  CALCULATE_INCOME_UNPROCESSABLE_MESSAGE,
  DISPUTE_PATH,
  INCOME_CALCULATION_PATH,
  SEND_REPORT_PATH
} from './constants';
import type { IncomeCalculationResponse } from './incomeVerificationSlice';
import { IncomeCalculationRequest } from './ts/interfaces';
import { convertToIncomeVerificationType } from './utils';

export type CalculateIncomeRejection = {
  code: string;
  message: string;
};

export const calculateIncomeThunk = createAsyncThunk<
  IncomeCalculationResponse,
  IncomeCalculationRequest,
  { rejectValue: CalculateIncomeRejection }
>(
  INCOME_CALCULATION_PATH,
  async ({ submissionId, documents }: IncomeCalculationRequest, { rejectWithValue }) => {
    try {
      // The SDK types calculateIncome as returning an AxiosResponse wrapper, but
      // a response interceptor unwraps it to the body at runtime — hence the cast.
      const response = (await api.calculateIncome(
        submissionId,
        documents
      )) as unknown as IncomeCalculationResponse;
      return response;
    } catch (error: any) {
      // 422 = no_processable_sources from the FVS combined endpoint (ITL-2575).
      // Surface as a rejection so the slice's `rejected` branch handles it,
      // not as a fulfilled action carrying an error body.
      if (error.response?.status === 422) {
        return rejectWithValue({
          code: 'no_processable_sources',
          message: CALCULATE_INCOME_UNPROCESSABLE_MESSAGE
        });
      }
      if (error.response?.data) {
        return error.response.data;
      } else {
        // Log only the error type, not the raw Axios error — its `config`
        // carries the request URL (with submissionId) and headers.
        console.error('calculateIncomeThunk failed', {
          path: INCOME_CALCULATION_PATH,
          errorType: error?.constructor?.name ?? 'unknown'
        });
        throw new Error('Error calculating income');
      }
    }
  }
);

export const sendReportThunk = createAsyncThunk(
  SEND_REPORT_PATH,
  async ({ entryId, documents }: { entryId: string; documents: IncomeVerification[] }) => {
    try {
      return api.sendReport(entryId, documents);
    } catch (error) {
      console.error(SEND_REPORT_PATH, error);
      throw new Error('Failed to send report');
    }
  }
);

export const disputeCalculationThunk = createAsyncThunk(DISPUTE_PATH, async (entryId: string) => {
  try {
    return api.appealCalculation(entryId);
  } catch (error) {
    console.error(DISPUTE_PATH, error);
    throw new Error(`Failed to dispute income calculation for entry: ${entryId}`);
  }
});

/**
 * Handles fetching and converting the information for the documents previously submitted for an appeal.
 *
 * @param entryId
 * @returns {Promise<IncomeVerification[]>}
 */
export async function getPreviousDocumentsForAppeal(
  entryId: string | undefined
): Promise<IncomeVerification[] | []> {
  let docs: IncomeVerification[] = [];
  if (!entryId) {
    return docs;
  }
  try {
    const result = await api.getAppealDocuments(entryId);
    const documents = result?.data || [];
    docs = documents.map((doc: IncomeCalculationDocument, index: number) => {
      const result = convertToIncomeVerificationType(doc);
      return {
        ...result,
        // Convert the dates to the expected format
        payPeriodStartDate: format(
          new Date(removeUtcTimeInString(result.payPeriodStartDate)),
          DATE_PICKER_FORMAT
        ),
        payPeriodEndDate: format(
          new Date(removeUtcTimeInString(result.payPeriodEndDate)),
          DATE_PICKER_FORMAT
        ),
        index: index
      };
    });
  } catch (error) {
    console.error('Failed to get data for appeal:', entryId);
    console.error(error);
  }

  return docs;
}

const removeUtcTimeInString = (dateString: string) => {
  //Removes the T as well as replaces the - with / which allows the time to not go back a day
  return dateString.replace(/-/g, '/').replace(/T.+/, '');
};
